import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types'; // Import PropTypes for type-checking React props

const Pagination = ({ pages, page, filterParam, baseUrl, search }) => {
  if (!pages || pages === 1) return null; // No pagination needed if pages is undefined/null or equals 1

  const getPageNumbers = () => {
    const totalPages = pages; // Total number of pages
    const currentPage = Number(page); // Current active page number
    const maxPageLinks = 5; // Maximum number of page links to display
    const pageNumbers = []; // Array to store the page numbers to display

    if (totalPages <= maxPageLinks) {
      // If total pages are less than or equal to the maximum, display all pages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i); // Add each page number to the array
      }
    } else {
      // When there are more pages than the maximum allowed
      const siblings = 1; // Number of pages to show on each side of the current page
      const leftBound = Math.max(currentPage - siblings, 2); // Calculate the left boundary, ensuring it's at least 2
      const rightBound = Math.min(currentPage + siblings, totalPages - 1); // Calculate the right boundary, ensuring it doesn't exceed totalPages - 1
      const showLeftEllipsis = leftBound > 2; // Determine if left ellipsis is needed
      const showRightEllipsis = rightBound < totalPages - 1; // Determine if right ellipsis is needed

      pageNumbers.push(1); // Always show the first page

      if (showLeftEllipsis) {
        pageNumbers.push('...'); // Add left ellipsis if there's a gap after the first page
      }

      for (let i = leftBound; i <= rightBound; i++) {
        pageNumbers.push(i); // Add the middle page numbers around the current page
      }

      if (showRightEllipsis) {
        pageNumbers.push('...'); // Add right ellipsis if there's a gap before the last page
      }

      pageNumbers.push(totalPages); // Always show the last page
    }

    return pageNumbers; // Return the array of page numbers to display
  };

  const pageNumbers = getPageNumbers(); // Generate the page numbers based on the current state

  const constructLink = (x) => {
    const params = new URLSearchParams();
    if (filterParam) {
      params.append('filter', filterParam); // Adds the filter to the query parameters if it exists
    }
    if (search) {
      params.append('search', search); // Adds the search term to the query parameters if it exists
    }
    params.append('page', x); // Adds the page number to the query parameters
    return `${baseUrl}?${params.toString()}`; // Returns the complete URL with query parameters
  }; // Function to construct URL with search and page parameters

  return (
    <div className="pagination-container">
      <Link
        className={`pagination-button ${
          page <= 1 ? 'inactive-pagination-button' : ''
        }`}
        to={constructLink(parseInt(page) - 1)} // Uses the constructLink function to generate the URL for the "Previous" button, including search if applicable
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
          <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="48"
            d="M328 112L184 256l144 144"
          />
        </svg>
      </Link>

      {pageNumbers.map((x, index) =>
        x === '...' ? (
          <span key={`ellipsis-${index}`} className="pagination-ellipsis">
            ...
          </span>
        ) : (
          <Link
            className={x === Number(page) ? 'active-page' : 'pages'}
            key={x}
            to={constructLink(x)} // Uses the constructLink function to generate the URL for to the button that leads to a specific page, including search if applicable
          >
            {x}
          </Link>
        )
      )}

      <Link
        className={`pagination-button ${
          page >= pages ? 'inactive-pagination-button' : ''
        }`}
        to={constructLink(parseInt(page) + 1)} // Uses the constructLink function to generate the URL for the "Next" button, including search if applicable
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
          <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="48"
            d="m184 112l144 144l-144 144"
          />
        </svg>
      </Link>
    </div>
  );
};

Pagination.propTypes = {
  pages: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  baseUrl: PropTypes.string.isRequired,
  search: PropTypes.string, // Optional search term to include in pagination links
}; // Define PropTypes to enforce the expected types of props passed to the Pagination component

export default Pagination;
